<!-- PROVISÓRIO (R2-X-4 = B): the kit has no non-modal drawer. This frame stands in for the registry Sheet under the
     kit's EditorDrawer and EditSheet until the kit's Sheet (or Inspector) takes `modal=false`; then the two drawers
     go back to the Sheet and this file goes away.
     Behavior, decision R2-X-3 = A: from 1280px the drawer is a column beside the content (the page lays it out in its
     own flex row); below 1280px it is the non-modal overlay of phase 3.3 (ConversationSidebar.vue): fixed, full
     height, at the inline end, with a shadow, closed by a click outside with the same ignore list. Not the modal
     Sheet: its focus trap and pointer lock would block the woot's dialogs and popovers, teleported to the body,
     opened from inside the drawer. Classes and the close button are the ported Sheet's (ui/sheet/SheetContent.vue). -->
<script setup>
// The frame only says "someone asked to close" (the close button, Escape, a click outside the overlay); the drawer
// that owns it decides, through its discard guard. `mode="overlay"` keeps the overlay at every width (the article
// editor "over everything", D-K3). Width: `column-class` (a literal `w-[…]`) and `overlay-class` (a literal
// `sm:max-w-[…]`), so Tailwind sees them.
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMediaQuery } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { Close16 } from '@carbon/icons-vue';

const props = defineProps({
  open: Boolean,
  label: { type: String, required: true }, // the accessible name of the drawer: "Scenario: Refund"
  mode: {
    type: String,
    default: 'auto',
    validator: value => ['auto', 'overlay'].includes(value),
  },
  columnClass: { type: String, default: '' },
  overlayClass: { type: String, default: '' },
});
const emit = defineEmits(['close']);
// Closed, the root is an empty fragment: attributes (data-editor-drawer, …) go on the aside by hand.
defineOptions({ inheritAttrs: false });
const { t } = useI18n();

const wide = useMediaQuery('(min-width: 1280px)');
const column = computed(() => props.mode === 'auto' && wide.value);

// The same exceptions as the conversation panel (3.3), plus the woot's native dialogs and FloatingVue popovers: a
// click in something the drawer opened is not a click outside it.
const IGNORE = [
  'dialog',
  '[data-popover-content]',
  '[data-popover-backdrop]',
  '[data-reka-popper-content-wrapper]',
  '.v-popper__popper',
];
const onOutside = () => {
  if (props.open && !column.value) emit('close');
};
const onEscape = event => {
  if (!event.defaultPrevented) emit('close');
};

// Not a modal: no focus trap. The focus goes into the drawer when it opens (its first field, as the Sheet does)
// and back to whoever opened it when it closes, but only when the focus was still in the drawer (✕, Escape, Save)
// or nowhere: a click outside that closed the overlay leaves the focus where that click put it (the list's search
// field, another row), as the 3.3 panel does. The watch runs before the DOM update, so the panel is still there.
const panel = ref(null);
let opener = null;
const FOCUSABLE =
  'input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]):not([data-drawer-close]), [tabindex]:not([tabindex="-1"])';
watch(
  () => props.open,
  async value => {
    if (value) {
      opener = document.activeElement;
      await nextTick();
      (panel.value?.querySelector(FOCUSABLE) ?? panel.value)?.focus();
    } else {
      const active = document.activeElement;
      const focusWasOurs =
        !active || active === document.body || panel.value?.contains(active);
      if (focusWasOurs && opener?.isConnected) opener.focus();
      opener = null;
    }
  },
  { immediate: true }
);
</script>

<template>
  <aside
    v-if="open"
    ref="panel"
    v-bind="$attrs"
    v-on-click-outside="[onOutside, { ignore: IGNORE }]"
    :role="column ? undefined : 'dialog'"
    :aria-modal="column ? undefined : 'false'"
    :aria-label="label"
    tabindex="-1"
    data-drawer-frame
    :data-mode="column ? 'column' : 'overlay'"
    :class="
      column
        ? [
            'relative flex h-full min-h-0 shrink-0 flex-col border-solid border-polaris-border bg-polaris-background text-polaris-foreground outline-none ltr:border-l rtl:border-r',
            columnClass,
          ]
        : [
            'fixed inset-y-0 z-40 flex h-full w-full flex-col border-solid border-polaris-border bg-polaris-background text-polaris-foreground shadow-lg outline-none ltr:right-0 ltr:border-l rtl:left-0 rtl:border-r',
            overlayClass,
          ]
    "
    @keydown.esc="onEscape"
  >
    <slot />
    <button
      type="button"
      data-drawer-close
      class="absolute top-3 flex size-6 items-center justify-center rounded-polaris-sm text-polaris-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-[2px] focus-visible:ring-polaris-ring p-0 ltr:right-3 rtl:left-3"
      :aria-label="t('POLARIS.DIALOG.CLOSE')"
      @click="emit('close')"
    >
      <Close16 class="size-4 p-0" aria-hidden="true" />
    </button>
  </aside>
  <template v-else />
</template>
